"use client";

import { OffersOffer } from '@/services/dataTypes';
import React, { useEffect, useState } from 'react';
import { filterOfferBanners, getBannerDimensions } from '@/constants/hooks';
import Banner from './Banner';
import { apiCategoryOfferBanners } from '@/apis/user';

interface Props {
    bannerResponse: OffersOffer[];
    domain: string;
    mer_slug: string;
    slug_type: string;
    categoryId: string;
    companyId: string;
}

const VerticalCategoryOfferBanner = ({ bannerResponse, domain, mer_slug, slug_type, categoryId, companyId }: Props) => {
    const [banners, setBanners] = useState<OffersOffer[]>([]);
    const [page, setPage] = useState(2);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        setBanners(filterOfferBanners(bannerResponse, 50, 2000, 65, 2000));
    }, [bannerResponse]);

    const loadMore = async () => {
        if (loading || !hasMore) return;
        setLoading(true);
        try {
            const res = await apiCategoryOfferBanners(categoryId, companyId, page);
            const newBanners = filterOfferBanners(res.data?.offers || [], 50, 2000, 65, 2000);
            setBanners(prev => [...prev, ...newBanners]);
            if (res?.data?.pagination?.next_page) { setPage(p => p + 1); setIsExpanded(true); }
            else setHasMore(false);
        } catch { /* silent */ }
        finally { setLoading(false); }
    };

    const showLess = () => {
        setBanners(filterOfferBanners(bannerResponse, 50, 2000, 65, 2000));
        setPage(2); setHasMore(true); setIsExpanded(false);
    };

    if (!banners?.length) return null;

    return (
        <div
            className="rounded-2xl overflow-hidden"
            style={{ border: '1px solid #E8F5EE', background: '#FFFFFF' }}
        >
            {/* Header */}
            <div
                className="flex items-center gap-2.5 px-4 py-3"
                style={{ background: '#F7FBF9', borderBottom: '1px solid #E8F5EE' }}
            >
                <div className="w-1 h-4 rounded-full bg-[#5D27A1]" />
                <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: '#1E293B' }}>
                    Banner Offers
                </span>
                <span
                    className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full"
                    style={{ background: '#E8F5EE', color: '#5D27A1' }}
                >
                    {banners.length}
                </span>
            </div>

            {/* Banners */}
            <div className="p-3 flex flex-col gap-2.5">
                {banners.map((offer_data, i) => {
                    const dimension = getBannerDimensions(offer_data);
                    return (
                        <div
                            key={i}
                            className="rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
                            style={{ border: '1px solid #E8F5EE' }}
                        >
                            <Banner
                                data={offer_data}
                                height={dimension?.height}
                                mer_slug={mer_slug}
                                slug_type={slug_type}
                                domain={domain}
                                width={dimension?.width}
                            />
                        </div>
                    );
                })}
            </div>

            {/* Controls */}
            {(hasMore || isExpanded) && (
                <div className="px-3 pb-3 flex items-center gap-2">
                    {hasMore && (
                        <button
                            onClick={loadMore}
                            disabled={loading}
                            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-[11px] font-bold uppercase tracking-widest transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md disabled:opacity-60"
                            style={{
                                background: 'linear-gradient(135deg, #5D27A1 0%, #471d7c 100%)',
                                color: '#FFFFFF',
                            }}
                        >
                            {loading
                                ? <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                : <>
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                                    </svg>
                                    Show More
                                </>
                            }
                        </button>
                    )}
                    {isExpanded && (
                        <button
                            onClick={showLess}
                            className="flex-1 flex items-center justify-center py-2.5 rounded-xl text-[11px] font-bold uppercase tracking-widest transition-all duration-300 hover:bg-[#F7FBF9]"
                            style={{ border: '1px solid #E8F5EE', color: '#94A3B8' }}
                        >
                            Show Less
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default VerticalCategoryOfferBanner;
